import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Users, Plus, Globe, Lock, ArrowUpRight } from "lucide-react";

const Groups = () => {
  const [mine, setMine] = useState([]);
  const [discover, setDiscover] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", public: true });

  const load = async () => {
    const [m, d] = await Promise.all([api.get("/groups"), api.get("/groups/discover")]);
    setMine(m.data); setDiscover(d.data);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/groups", form);
      toast.success("Group created");
      setCreating(false); setForm({ name: "", description: "", public: true });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const join = async (id) => {
    try { await api.post(`/groups/join/${id}`); toast.success("Joined"); load(); } catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto">
      <header className="mb-10 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-mono mb-3">/ groups</div>
          <h1 className="font-display text-4xl md:text-5xl tightest font-semibold leading-none">Study together.</h1>
          <p className="text-neutral-500 mt-3 text-[0.92rem]">Invite classmates via @username. They accept or decline — no spam.</p>
        </div>
        <button onClick={() => setCreating(!creating)} className="btn-primary" data-testid="new-group"><Plus size={14}/> New group</button>
      </header>

      {creating && (
        <form onSubmit={create} className="glass-strong p-6 mb-10 space-y-4" data-testid="new-group-form">
          <input className="input-cyber" placeholder="Group name" value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} required data-testid="group-name"/>
          <input className="input-cyber" placeholder="Description" value={form.description} onChange={(e)=>setForm({...form, description:e.target.value})} data-testid="group-desc"/>
          <label className="flex items-center gap-2 text-[0.86rem] text-neutral-400">
            <input type="checkbox" checked={form.public} onChange={(e)=>setForm({...form, public:e.target.checked})} data-testid="group-public"/>
            Public (anyone can join)
          </label>
          <button type="submit" className="btn-primary" data-testid="group-create-submit">Create</button>
        </form>
      )}

      <section className="mb-12">
        <div className="label-mono mb-3">/ mine</div>
        <h2 className="font-display text-xl tracking-tight mb-4">My groups</h2>
        <div className="grid md:grid-cols-3 gap-3">
          {mine.length === 0 && <div className="text-neutral-500 text-[0.86rem] col-span-full">You haven&apos;t joined any group yet.</div>}
          {mine.map((g) => (
            <Link to={`/groups/${g.id}`} key={g.id} className="glass p-6 hover:border-white/20 transition-colors group" data-testid={`group-${g.id}`}>
              <div className="flex items-center justify-between mb-3">
                <Users size={16} className="text-neutral-400 group-hover:text-white transition-colors"/>
                <div className="flex items-center gap-2">
                  {g.public ? <Globe size={12} className="text-neutral-500"/> : <Lock size={12} className="text-neutral-500"/>}
                  <ArrowUpRight size={13} className="text-neutral-600 group-hover:text-white transition-colors"/>
                </div>
              </div>
              <div className="font-medium text-[0.95rem]">{g.name}</div>
              <div className="text-neutral-500 text-[0.82rem] mt-1 line-clamp-2">{g.description || "—"}</div>
              <div className="label-mono mt-4">{g.members?.length || 0} members</div>
            </Link>
          ))}
        </div>
      </section>

      <section>
        <div className="label-mono mb-3">/ discover</div>
        <h2 className="font-display text-xl tracking-tight mb-4">Public groups</h2>
        <div className="grid md:grid-cols-3 gap-3">
          {discover.filter(g => !mine.find(m=>m.id===g.id)).length === 0 && (
            <div className="text-neutral-500 text-[0.86rem] col-span-full">No public groups yet.</div>
          )}
          {discover.filter(g => !mine.find(m=>m.id===g.id)).map((g) => (
            <div key={g.id} className="glass p-6" data-testid={`discover-${g.id}`}>
              <Users size={16} className="text-neutral-400 mb-3"/>
              <div className="font-medium text-[0.95rem]">{g.name}</div>
              <div className="text-neutral-500 text-[0.82rem] mt-1 mb-4 line-clamp-2">{g.description || "—"}</div>
              <button onClick={() => join(g.id)} className="btn-secondary text-[0.75rem] py-1.5 px-3" data-testid={`join-${g.id}`}>Join</button>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Groups;
